// src/pages/inicioOperaciones.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createApiClient } from '../lib/apiClient';
import { notifySuccess } from "../lib/notifications";
import { useLocation } from "react-router-dom";
import {
  FiTruck,
  FiRefreshCw,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiX,
  FiSave,
  FiDownload,
  FiCalendar,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useUserId } from "../UserIdContext";
import {
  readDepositoFromSearch,
  resolveDepositoRoute,
} from "../lib/depositoRoute";
import "./usuarios.css";
import "./StatusCard.css";
import "./OccupationCards.css";
import DapTruckIcon from "./DapTruckIcon";
import { separateDashboardDap } from "../lib/dashboardDap";

/* ================== THEME ================== */
const DASH_STYLES = `
:root{
  --bg:#050c13;
  --panel:#0b1520;
  --panel-soft:#0d1a26;
  --panel-strong:rgba(13,25,36,.85);
  --panel-solid:#0d1924;
  --stroke:#132433;
  --primary:#0FB872;
  --primary-soft:#13332a;
  --accent:#65f9a1;
  --danger:#ff5a67;
  --glow:rgba(0,255,170,.16);
  --glow-soft:rgba(0,255,170,.08);
  --text:#f2f7ff;
  --muted:#8499b8;
  --radius:18px;
  --shadow:0 28px 90px rgba(0,0,0,.55);
  --t:.25s cubic-bezier(.2,.7,.2,1);
}

.dashboard-wrap{ padding:16px; color:var(--text); }
.dash-header{ display:flex; justify-content:space-between; align-items:flex-end; gap:16px; flex-wrap:wrap; margin-bottom:16px; }
.dash-title h1{ margin:0; font-size:clamp(22px,2vw,32px); font-weight:800; }
.dash-title small{ color:var(--muted); }
.dash-actions{ display:flex; gap:10px; flex-wrap:wrap; align-items:center; }

.dash-btn{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:8px 14px;
  border-radius:14px;
  border:1px solid rgba(255,255,255,.08);
  background:rgba(255,255,255,.03);
  color:var(--text);
  cursor:pointer;
  font-size:13px;
}
.dash-btn svg{ font-size:16px; }

.date-pill{
  display:flex; align-items:center; gap:10px;
  border:1px solid rgba(255,255,255,.08);
  background:rgba(255,255,255,.03);
  padding:8px 12px;
  border-radius:14px;
}
.date-pill label{ font-size:12px; color:var(--muted); display:flex; align-items:center; gap:8px; }
.date-pill input{
  background:transparent;
  border:0;
  outline:0;
  color:var(--text);
  font-size:13px;
}

.cards-row{
  display:grid;
  grid-template-columns:repeat(4, minmax(0,1fr));
  gap:12px;
}

.dashboard-wrap .card{
  background:radial-gradient(600px 280px at -40% -60%, rgba(15,184,114,.16), transparent 60%), var(--panel);
  border-radius:var(--radius);
  border:2px solid #33cc99;
  padding:12px 14px 14px;
  box-shadow:0 0 0 1px rgba(51,204,153,.25), 0 10px 24px var(--glow-soft), 0 18px 48px rgba(0,0,0,.45);
  display:flex;
  flex-direction:column;
  gap:10px;
  position:relative;
  isolation:isolate;
  overflow:visible;
}

.dashboard-wrap .card::before{
  content:"";
  position:absolute;
  inset:-12px;
  border-radius:calc(var(--radius) + 12px);
  background:radial-gradient(90% 120% at 50% 50%, var(--glow), transparent 72%);
  z-index:-1;
  pointer-events:none;
}

.donut-wrap{
  width:138px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:flex-start;
}
.donut-meter{
  width:138px;
  height:80px;
  overflow:visible;
}
.donut-label{
  margin-top:-2px;
  display:flex;
  align-items:center;
  justify-content:center;
  flex-direction:column;
  line-height:1.1;
}
.donut-value{
  font-size:18px;
  font-weight:800;
}

.dashboard-wrap .graph-panel{
  margin-top:0;
  padding:14px;
  border-radius:var(--radius);
  border:2px solid #33cc99;
  background:var(--panel-soft);
  box-shadow:0 0 0 1px rgba(51,204,153,.2), 0 10px 24px var(--glow-soft), 0 18px 48px rgba(0,0,0,.45);
  position:relative;
  isolation:isolate;
  overflow:visible;
}

.dashboard-wrap .graph-panel::before{
  content:"";
  position:absolute;
  inset:-12px;
  border-radius:calc(var(--radius) + 12px);
  background:radial-gradient(90% 120% at 50% 50%, var(--glow), transparent 72%);
  z-index:-1;
  pointer-events:none;
}

.section-block{ margin-top:16px; }
.section-head{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:16px;
  flex-wrap:wrap;
  margin-bottom:10px;
}
.section-title{
  margin:0 0 0 2px;
  font-size:clamp(22px,2vw,32px);
  font-weight:800;
  line-height:1.2;
}
.section-range-controls{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
  align-items:stretch;
  justify-content:flex-end;
}

.graph-header{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:12px;
  flex-wrap:wrap;
  margin-bottom:12px;
}
.graph-header h2{ margin:0; font-size:clamp(22px,2vw,32px); font-weight:800; }
.graph-header small{ color:var(--muted); }
.graph-header--legend{
  justify-content:flex-end;
  align-items:center;
}
.graph-copy{
  display:flex;
  flex-direction:column;
  gap:10px;
  flex:1 1 420px;
  min-width:260px;
}
.graph-controls{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
  align-items:flex-end;
}
.range-date-pill{
  min-width:170px;
  padding:10px 12px;
  border-radius:16px;
  border:1px solid rgba(255,255,255,.08);
  background:linear-gradient(180deg, rgba(255,255,255,.05), rgba(255,255,255,.02));
  box-shadow:inset 0 1px 0 rgba(255,255,255,.04);
}
.range-date-pill label{
  display:flex;
  align-items:center;
  gap:8px;
  font-size:11px;
  letter-spacing:.04em;
  text-transform:uppercase;
  color:var(--muted);
  margin-bottom:6px;
}
.range-date-pill input{
  width:100%;
  background:transparent;
  border:0;
  outline:0;
  color:var(--text);
  font-size:13px;
  font-weight:600;
  font-family:inherit;
}
.range-date-pill input::-webkit-calendar-picker-indicator{
  cursor:pointer;
  filter:invert(1) brightness(.85);
  opacity:.85;
}
.range-hint{
  font-size:11px;
  color:var(--muted);
}
.graph-legend{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
  font-size:12px;
  color:var(--muted);
}
.legend-pill{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:6px 10px;
  border-radius:999px;
  border:1px solid rgba(255,255,255,.08);
  background:rgba(255,255,255,.03);
}
.legend-dot{
  width:10px; height:10px; border-radius:999px;
  background:rgba(255,255,255,.2);
  border:1px solid rgba(255,255,255,.15);
}

.custom-tooltip{
  background:#020617;
  border-radius:10px;
  padding:8px 10px;
  border:1px solid rgba(148,163,184,.5);
  font-size:11px;
}

.truck-table{
  width:100%;
  border-collapse:collapse;
  font-size:13px;
}
.truck-table th,
.truck-table td{
  padding:8px 10px;
  border-bottom:1px solid rgba(148,163,184,.3);
  text-align:left;
}
.truck-table th{ color:var(--muted); font-weight:600; }
.truck-table td strong{ font-weight:700; }

.summary-layout{
  display:block;
  width:100%;
}

.summary-panel,
.summary-status-slot{
  min-width:0;
}

.summary-panel{
  width:100%;
  max-width:none;
  box-sizing:border-box;
}

/* ============ OCUPACION ============ */
.ocu-topbar{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
  margin-bottom:10px;
}
.ocu-topbar--actions{
  justify-content:space-between;
  align-items:flex-start;
}
.ocu-topbar-copy{
  display:flex;
  flex-direction:column;
  gap:8px;
  flex:1 1 280px;
}
.ocu-topbar-note{
  font-size:12px;
  color:var(--muted);
}
.ocu-actions{ display:flex; gap:10px; flex-wrap:wrap; align-items:center; }
.ocu-segment{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:4px;
  border-radius:16px;
  border:1px solid rgba(255,255,255,.08);
  background:rgba(255,255,255,.03);
  width:fit-content;
}
.ocu-segment-btn{
  border:0;
  border-radius:12px;
  padding:8px 12px;
  background:transparent;
  color:var(--muted);
  font-size:12px;
  font-weight:700;
  cursor:pointer;
  transition:background var(--t), color var(--t), box-shadow var(--t);
}
.ocu-segment-btn.active{
  background:linear-gradient(160deg, rgba(15,184,114,.22), rgba(56,189,248,.18));
  color:var(--text);
  box-shadow:inset 0 0 0 1px rgba(15,184,114,.28);
}
.ocu-grid{
  display:grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap:10px;
}
@media (max-width: 1300px){
  .ocu-grid{ grid-template-columns:repeat(3, minmax(0,1fr)); }
}
@media (max-width: 1100px){
  .cards-row{ grid-template-columns:repeat(2, minmax(0,1fr)); }
  .ocu-grid{ grid-template-columns:repeat(2, minmax(0,1fr)); }
  .summary-layout{ grid-template-columns:1fr; }
  .section-head{ align-items:flex-start; }
  .section-range-controls{
    width:100%;
    justify-content:flex-start;
  }
}
@media (max-width: 620px){
  .cards-row{ grid-template-columns:1fr; }
  .ocu-grid{ grid-template-columns:1fr; }
}

.ocu-card{
  background:radial-gradient(600px 280px at -40% -60%, rgba(56,189,248,.14), transparent 60%), var(--panel);
  border-radius:var(--radius);
  border:1px solid var(--stroke);
  padding:10px;
  min-width:0;
  box-shadow:0 18px 48px rgba(0,0,0,.45);
  position:relative;
  overflow:hidden;
}
.ocu-card.actionable{
  cursor:pointer;
  transition:transform var(--t), border-color var(--t), box-shadow var(--t);
}
.ocu-card.actionable:hover{
  transform:translateY(-2px);
  border-color:rgba(56,189,248,.42);
  box-shadow:0 24px 54px rgba(0,0,0,.5);
}
.ocu-card.actionable:focus-visible{
  outline:0;
  border-color:rgba(15,184,114,.62);
  box-shadow:0 0 0 2px rgba(15,184,114,.18), 0 24px 54px rgba(0,0,0,.5);
}
.ocu-card-header{ display:flex; align-items:flex-start; justify-content:space-between; gap:8px; }
.ocu-title{ font-weight:800; font-size:13px; margin:0; min-width:0; overflow-wrap:anywhere; }
.ocu-sub{ margin-top:4px; font-size:12px; color:var(--muted); }
.ocu-metrics{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:10px;
  margin-top:12px;
}
.ocu-pct{ font-size:26px; font-weight:900; letter-spacing:-.5px; }
.ocu-bar{
  width:100%;
  height:10px;
  border-radius:999px;
  background:rgba(255,255,255,.06);
  border:1px solid rgba(255,255,255,.08);
  overflow:hidden;
  margin-top:10px;
}
.ocu-bar > div{
  height:100%;
  border-radius:999px;
  background: linear-gradient(90deg, rgba(15,184,114,.95), rgba(56,189,248,.95));
  width:0%;
}
.ocu-mini{
  display:flex;
  flex-direction:column;
  gap:4px;
  font-size:12px;
  color:var(--muted);
  text-align:right;
}
.ocu-tools{
  display:flex;
  flex:0 0 auto;
  gap:4px;
}
.ocu-tools .icon-btn{ width:24px; height:24px; border-radius:8px; padding:0; }
.ocu-tools .icon-btn svg{ width:13px; height:13px; }

.icon-btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:30px;
  height:30px;
  border-radius:12px;
  border:1px solid rgba(255,255,255,.10);
  background:rgba(255,255,255,.04);
  color:var(--text);
  cursor:pointer;
}
.icon-btn:hover{ border-color: rgba(15,184,114,.55); }
.ocu-empty{
  margin-top:10px;
  padding:18px;
  border-radius:16px;
  border:1px dashed rgba(255,255,255,.12);
  background:rgba(255,255,255,.02);
  color:var(--muted);
  font-size:13px;
}

.modal-backdrop{
  position:fixed;
  inset:0;
  background:radial-gradient(160% 120% at 50% 10%, rgba(101,249,161,.06), transparent 45%), rgba(0,0,0,.7);
  backdrop-filter:blur(4px);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:9999;
  padding:16px;
}
.modal-card{
  width:min(520px, 96vw);
  border-radius:22px;
  border:1px solid rgba(42,71,90,.7);
  background:linear-gradient(180deg, rgba(16,26,34,.96), rgba(8,14,22,.94));
  box-shadow:0 36px 120px rgba(0,0,0,.62), 0 0 0 1px rgba(255,255,255,.02) inset;
  overflow:hidden;
}
.modal-head{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:10px;
  padding:14px 16px;
  border-bottom:1px solid rgba(17,49,67,.8);
  background:linear-gradient(180deg, rgba(255,255,255,.02), rgba(255,255,255,0));
}
.modal-head strong{ font-size:13px; }
.modal-body{
  padding:16px 18px 18px;
  display:flex;
  flex-direction:column;
  gap:10px;
  background:radial-gradient(140% 140% at 4% 0%, rgba(101,249,161,.06), transparent 32%), var(--panel-solid);
}
.field{ display:flex; flex-direction:column; gap:6px; }
.field label{ font-size:12px; color:var(--muted); font-weight:700; }
.ocu-form-note{
  font-size:11px;
  color:var(--muted);
}
.input{
  width:100%;
  padding:10px 12px;
  border-radius:12px;
  border:1px solid var(--stroke);
  background:var(--panel-strong);
  color:var(--text);
  outline:none;
  transition:border-color var(--t), background var(--t), box-shadow var(--t);
}
.input:focus{
  border-color:#1f6a50;
  background:rgba(13,25,36,.92);
  box-shadow:0 0 0 3px rgba(10,156,94,.18);
}
.modal-actions{
  display:flex;
  justify-content:flex-end;
  gap:10px;
  padding:12px 16px;
  border-top:1px solid rgba(17,49,67,.8);
  background:linear-gradient(180deg, rgba(10,16,24,.98), rgba(10,16,24,.92));
  flex-wrap:wrap;
}
.btn{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:9px 12px;
  border-radius:14px;
  border:1px solid rgba(255,255,255,.10);
  background:rgba(255,255,255,.03);
  color:var(--text);
  cursor:pointer;
  font-size:13px;
}
.btn-primary{
  border-color: rgba(15,184,114,.40);
  background: rgba(15,184,114,.14);
}
.btn-danger{
  border-color: rgba(239,68,68,.35);
  background: rgba(239,68,68,.10);
}

.modal-actions .btn{
  border-radius:12px;
  padding:10px 14px;
  border:1px solid var(--stroke);
  background:var(--panel-strong);
  color:var(--text);
  box-shadow:0 10px 24px rgba(0,0,0,.35);
}
.modal-actions .btn-primary{
  background:linear-gradient(160deg, #0fb872, #0a9c5e);
  border-color:transparent;
  color:#04140e;
  font-weight:700;
}
.modal-actions .btn-primary:hover{
  box-shadow:0 12px 30px rgba(15,184,114,.35);
}
.ocu-detail-summary{
  display:grid;
  grid-template-columns:repeat(4, minmax(0, 1fr));
  gap:10px;
}
.ocu-detail-kpi{
  border:1px solid rgba(255,255,255,.08);
  border-radius:16px;
  padding:12px;
  background:rgba(255,255,255,.03);
  display:flex;
  flex-direction:column;
  gap:4px;
}
.ocu-detail-kpi span{
  font-size:11px;
  color:var(--muted);
  text-transform:uppercase;
  letter-spacing:.06em;
}
.ocu-detail-kpi strong{
  font-size:22px;
  line-height:1;
}
.ocu-detail-note{
  font-size:12px;
  color:var(--muted);
}
.ocu-detail-state{
  padding:14px 16px;
  border-radius:16px;
  border:1px dashed rgba(255,255,255,.12);
  background:rgba(255,255,255,.02);
  color:var(--muted);
  font-size:13px;
}
.ocu-detail-state.error{
  border-color:rgba(255,90,103,.35);
  color:#ffd7dc;
  background:rgba(255,90,103,.08);
}
.ocu-detail-table-wrap{
  border:1px solid rgba(255,255,255,.08);
  border-radius:18px;
  overflow:auto;
  background:rgba(255,255,255,.02);
}
.ocu-detail-table{
  width:100%;
  border-collapse:collapse;
  border-spacing:0;
}
.ocu-detail-table th,
.ocu-detail-table td{
  padding:12px 14px;
  border-bottom:1px solid rgba(255,255,255,.08);
  text-align:left;
  font-size:13px;
}
.ocu-detail-table th{
  color:var(--muted);
  font-weight:700;
  background:rgba(255,255,255,.03);
}
.ocu-detail-table td:nth-child(2),
.ocu-detail-table th:nth-child(2),
.ocu-detail-table td:last-child,
.ocu-detail-table th:last-child{
  text-align:right;
}
.ocu-detail-table tbody tr:last-child td{
  border-bottom:0;
}
.ocu-detail-client{
  font-weight:700;
  color:var(--text);
}
.ocu-detail-excess{
  color:var(--danger);
  font-weight:800;
}
@media (max-width: 760px){
  .ocu-detail-summary{
    grid-template-columns:repeat(2, minmax(0, 1fr));
  }
}
`;

const DASH_STYLE_ID = "inicio-operaciones-dashboard-styles";

/* Inject styles once */
function useDashStyles() {
  useEffect(() => {
    if (!document.getElementById(DASH_STYLE_ID)) {
      const tag = document.createElement("style");
      tag.id = DASH_STYLE_ID;
      tag.textContent = DASH_STYLES;
      document.head.appendChild(tag);
    }
    return () => {
      document.getElementById(DASH_STYLE_ID)?.remove();
    };
  }, []);
}

/* ================== HELPERS ================== */

const api = createApiClient({
  baseURL:
    process.env.REACT_APP_OPS_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    "https://dnbinderapi.pruebasistemasgenius.com",
  headers: { "Content-Type": "application/json" },
});

const ymdToDateUTC = (ymd) => new Date(ymd + "T00:00:00.000Z");
const fechaUTC = (d = new Date()) => new Date(d).toISOString().slice(0, 10);
const fechaLocalYMD = (d = new Date()) => {
  const date = new Date(d);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const addDaysUTC = (ymd, n) => {
  const d = ymdToDateUTC(ymd);
  d.setUTCDate(d.getUTCDate() + n);
  return fechaUTC(d);
};
const fmtLabel = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const DEFAULT_FLOW_RANGE_DAYS = 7;
const buildDateRangeUTC = (desdeStr, hastaStr) => {
  if (!desdeStr || !hastaStr || desdeStr > hastaStr) return [];

  const fechas = [];
  const cursor = ymdToDateUTC(desdeStr);
  const end = ymdToDateUTC(hastaStr);

  while (cursor <= end) {
    fechas.push(fechaUTC(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return fechas;
};



// === LOGOS que fuerzan FINALIZADO ===
const FORCE_FINAL_LOGOS = new Set(
  [
    "documentosEnAdministracion.jpg",
    "salidaDeExportacionOK.jpg",

    "documentosEnAdministracion.png",
    "salidaDeExportacionOK.png",

    "operativaIngresoFinalizado.jpg",
    "operativaEgresoFinalizado.jpg",
    "operativaIngresoFinalizado.png",
    "operativaEgresoFinalizado.png",

  ].map((s) => s.toLowerCase())
);

// === LOGOS por estado ===
const PENDING_LOGOS = new Set(
  [
    "arribado1.jpg",
    "arribado2.jpg",
    "carga.jpg",
    "descarga.jpg",
    "inArribado.jpg",
    "outArribado.jpg",
    "outCarga.jpg",
    "inDescarga.jpg",
  ].map((f) => f.toLowerCase())
);

const PROCESS_LOGOS = new Set(
  [
    "enProcesoDeCarga.jpg",
    "enProcesoDeDescarga.jpg",
    "enprocesodecarga.jpg",
    "enprocesodedescarga.jpg",
    
  ].map((f) => f.toLowerCase())
);

const FINAL_LOGOS = new Set(
  [
    "finalizadoCargado.jpg",
    "finalizadoDescargado.jpg",
    "documentosEnAdministracion.jpg",
    "salidaDeExportacionOK.jpg",
    "cargaFinalizada.jpg",
    "descargaFinalizada.jpg",
    "operativaIngresoFinalizado.jpg",
    "operativaEgresoFinalizado.jpg",

  ].map((f) => f.toLowerCase())
);


function pickLogoName(raw) {
  if (!raw) return "";
  let s = String(raw).trim();

  // si viene URL, sacamos query/hash
  s = s.split("?")[0].split("#")[0];

  // nos quedamos con el nombre del archivo (sin ruta)
  s = s.replaceAll("\\", "/");
  const parts = s.split("/");
  s = parts[parts.length - 1] || "";

  // normalizar acentos: "Administración" => "Administracion"
  s = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  return s.toLowerCase();
}

function toInt(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getContenedoresCamion(op, camionIndex) {
  const conts = Array.isArray(op?.Contenedores) ? op.Contenedores : [];
  const targetIndex = toInt(camionIndex, 0);
  return conts.filter((c) => toInt(c?.camionIndex, 0) === targetIndex);
}

function inferTruckCount(op) {
  if (!op || op.soloComentario === true || op.esComentario === true || op.esSoloComentario === true) {
    return 0;
  }

  const explicitCount = toInt(op.cantidadCamiones, 0);
  const trucksCount = Array.isArray(op.camiones) ? op.camiones.length : 0;
  const fromContenedores = Array.isArray(op.Contenedores) && op.Contenedores.length
    ? Math.max(...op.Contenedores.map((c) => toInt(c?.camionIndex, 0))) + 1
    : 0;

  const inferred = Math.max(explicitCount, trucksCount, fromContenedores);
  if (inferred > 0) return inferred;

  const hasTruckData =
    !!pickLogoName(op.dibujo) ||
    (Array.isArray(op.camiones) && op.camiones.some(Boolean)) ||
    (Array.isArray(op.Contenedores) && op.Contenedores.length > 0);

  return hasTruckData ? 1 : 0;
}

function hasNonEmptyText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function hasOperacionComentario(op) {
  return [
    op?.comentarioGeneral,
    op?.comentario,
    op?.textoComentario,
    op?.observacion,
  ].some(hasNonEmptyText);
}

function isCommentOnlyOperation(op) {
  const cantCamiones = inferTruckCount(op);
  const sinContenedores =
    !Array.isArray(op?.Contenedores) || op.Contenedores.length === 0;

  return (
    op?.soloComentario === true ||
    op?.esComentario === true ||
    op?.esSoloComentario === true ||
    (cantCamiones === 0 && sinContenedores && hasOperacionComentario(op))
  );
}

function estadoPorLogo(raw) {
  const s = pickLogoName(raw);
  if (!s) return null;
  if (FORCE_FINAL_LOGOS.has(s)) return "FINALIZADO";
  if (FINAL_LOGOS.has(s)) return "FINALIZADO";
  if (PROCESS_LOGOS.has(s)) return "PROCESO";
  if (PENDING_LOGOS.has(s)) return "PENDIENTE";
  // si no lo conocemos, no forzamos
  return null;
}

/**
 * Determina el estado de UN camión dentro de una operación.
 * Orden de prioridad:
 * 1) Logo del camión (camiones[i].dibujo) o logo global de la op (op.dibujo)
 * 2) Contenedores del camión (si existen) -> finalizado si TODOS finalizados
 * 3) Flags del camión (finalizado/pendiente)
 * 4) default PROCESO
 */
function getEstadoCamion(op, camionIndex) {
  const targetIndex = toInt(camionIndex, 0);
  const camion =
    Array.isArray(op.camiones) &&
    op.camiones.find((c) => toInt(c?.index, -1) === targetIndex);
  const logo = camion?.dibujo || op.dibujo || "";
  const stLogo = estadoPorLogo(logo);

  // 2) Contenedores del camión
  const contCamion = getContenedoresCamion(op, targetIndex);
  const todosContFinal =
    contCamion.length > 0 &&
    contCamion.every((c) => (c.estado || "Completar") === "Finalizado");

  // 3) Flags
  const flagFinal = camion?.finalizado === true;
  const flagPend = camion?.pendiente === true;

  if (stLogo === "FINALIZADO" || todosContFinal || flagFinal) return "FINALIZADO";
  if (stLogo === "PROCESO" || stLogo === "PENDIENTE") return stLogo;
  if (flagPend) return "PENDIENTE";
  return "PROCESO";
}

// clasificar operación en una de las 4 tarjetas
function classifyGroup(op, deposito) {
  const tipo = String(op?.tipoOperacion || "").trim().toLowerCase().replace(/\s+/g, "");
  const dep = String(deposito || "binder1").trim().toLowerCase();

  if (tipo.startsWith("nacional")) return dep === "binder2" ? "nacional2" : "nacional1";
  if (tipo.startsWith("fiscalimpo") || tipo.startsWith("fiscalexpo")) {
    return dep === "binder2" ? "fiscal2" : "fiscal1";
  }
  return null;
}

function classifyObservacionGroup(obs, deposito) {
  const ambito = String(obs?.ambito || "").trim().toLowerCase();
  const dep = String(deposito || "binder1").trim().toLowerCase();

  if (ambito.startsWith("nacional")) return dep === "binder2" ? "nacional2" : "nacional1";
  if (ambito.startsWith("fiscal")) return dep === "binder2" ? "fiscal2" : "fiscal1";
  return null;
}

/* ================== COMPONENTES UI ================== */

function Donut({ percent }) {
  const pct = Math.max(0, Math.min(100, percent || 0));
  const centerX = 60;
  const centerY = 60;
  const radius = 40;
  const strokeWidth = 10;
  const circumference = 2 * Math.PI * radius;
  const semiCircumference = circumference / 2;
  const progressLength = semiCircumference * (pct / 100);
  const angle = 180 - (pct / 100) * 180;
  const needleLength = radius - 10;
  const needleRadians = (angle * Math.PI) / 180;
  const needleX = centerX + needleLength * Math.cos(needleRadians);
  const needleY = centerY - needleLength * Math.sin(needleRadians);

  return (
    <div className="donut-wrap">
      <svg className="donut-meter" viewBox="0 0 120 80">
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke="rgba(30,64,80,.6)"
          strokeWidth={strokeWidth}
          strokeDasharray={`${semiCircumference} ${circumference}`}
          transform={`rotate(180 ${centerX} ${centerY})`}
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke="#22c55e"
          strokeWidth={strokeWidth}
          strokeDasharray={`${progressLength} ${circumference}`}
          transform={`rotate(180 ${centerX} ${centerY})`}
          strokeLinecap="round"
        />
        <line
          x1={centerX}
          y1={centerY}
          x2={needleX}
          y2={needleY}
          stroke="#d6ffe6"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle
          cx={centerX}
          cy={centerY}
          r="7"
          fill="rgba(4,18,28,.95)"
          stroke="rgba(214,255,230,.45)"
          strokeWidth="2"
        />
        <circle cx={centerX} cy={centerY} r="3" fill="#d6ffe6" />
      </svg>

      <div className="donut-label">
        <span className="donut-value">{pct.toFixed(1)}%</span>
        <small style={{ fontSize: 11, color: "var(--muted)" }}>REALIZADOS</small>
      </div>
    </div>
  );
}

function StatusCard({ title, data }) {
  const total = data?.total || 0; // camiones
  const cerrados = data?.cerrados || 0; // camiones finalizados
  const ingresos = data?.ingresos || 0; // camiones de ingreso
  const egresos = data?.egresos || 0; // camiones de egreso
  const dapIngresos = data?.dapIngresos || 0;
  const dapEgresos = data?.dapEgresos || 0;
  const comentarios = data?.comentarios || 0; // comentarios del día
  const pct = total > 0 ? (cerrados * 100) / total : 0;

  return (
    <div
      className="card status-card"
      role="group"
      aria-label={title}
    >
      <div className="status-meter">
        <Donut percent={pct} />
      </div>

      <div className="status-heading">
        <div className="card-title">
          {title}
        </div>
      </div>

      <div className="card-metrics">
        <div className="status-truck-counters">
          <div className="status-truck-counter" title="Camiones IN sin D.A.P">
            <span className="status-truck-counter-line">
              <FiTruck aria-hidden="true" style={{ transform: "scaleX(1)" }} />
              <strong aria-label="Camiones IN sin D.A.P">{ingresos}</strong>
            </span>
          </div>
          <div className="status-truck-counter dap-counter" title="D.A.P IN: camión visto de frente">
            <span className="status-truck-counter-line">
              <DapTruckIcon direction="in" role="img" aria-label="Camión D.A.P IN: vista frontal" />
              <strong aria-label="Camiones D.A.P IN">{dapIngresos}</strong>
            </span>
          </div>
          <div className="status-truck-counter" title="Camiones OUT sin D.A.P">
            <span className="status-truck-counter-line">
              <FiTruck aria-hidden="true" style={{ transform: "scaleX(-1)" }} />
              <strong aria-label="Camiones OUT sin D.A.P">{egresos}</strong>
            </span>
          </div>
          <div className="status-truck-counter dap-counter" title="D.A.P OUT: camión visto desde atrás">
            <span className="status-truck-counter-line">
              <DapTruckIcon direction="out" role="img" aria-label="Camión D.A.P OUT: vista trasera" />
              <strong aria-label="Camiones D.A.P OUT">{dapEgresos}</strong>
            </span>
          </div>
        </div>

        <div className="status-comments">
          Comentarios: <strong>{comentarios}</strong>
        </div>
      </div>
    </div>
  );
}

const formatOcupaciones = (value) => {
  if (!value) return "";
  const list = Array.isArray(value) ? value : Array.from(value);
  const clean = list.map((item) => String(item || "").trim()).filter(Boolean);
  if (!clean.length) return "";
  const max = 3;
  if (clean.length <= max) return clean.join(", ");
  return `${clean.slice(0, max).join(", ")} +${clean.length - max}`;
};

const CustomTooltip4 = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const nacional1Ocu = formatOcupaciones(row.nacional1Ocu);
  const nacional2Ocu = formatOcupaciones(row.nacional2Ocu);

  return (
    <div className="custom-tooltip">
      <strong>{label}</strong>
      {payload.map((p) => {
        const extra =
          p.dataKey === "nacional1" ? nacional1Ocu : p.dataKey === "nacional2" ? nacional2Ocu : "";
        return (
          <div key={p.dataKey} style={{ marginTop: 4 }}>
            <div>
              {p.name}: <strong>{p.value ?? 0}</strong>
            </div>
            {extra ? (
              <div style={{ fontSize: 10, color: "var(--muted)" }}>Factor: {extra}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

function Modal({ open, title, onClose, children, footer, width = "min(520px, 96vw)" }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-card" style={{ width }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <strong>{title}</strong>
          <button className="icon-btn" type="button" onClick={onClose} title="Cerrar">
            <FiX />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-actions">{footer}</div>}
      </div>
    </div>
  );
}

/* ================== OCUPACION API Y CACHE DE LECTURA ================== */

const LS_KEY_OCU = "ops_ocupaciones_v1";
const normalizeOcupacionDeposito = (deposito) =>
  String(deposito || "").trim().toLowerCase() === "binder2" ? "binder2" : "binder1";
const binderLabel = (deposito) =>
  normalizeOcupacionDeposito(deposito) === "binder2" ? "Binder 2" : "Binder 1";
const buildOcupacionesStorageKey = (deposito) =>
  `${LS_KEY_OCU}:${normalizeOcupacionDeposito(deposito)}`;

function normalizeOcupacion(item, depositoFallback = "binder1") {
  if (!item) return null;
  return {
    _id: item._id || item.id || item.uuid || `${item.nombre}-${item.ocupacionTotal}`,
    deposito: normalizeOcupacionDeposito(item.deposito || depositoFallback),
    nombre: item.nombre || item.sucursal || "Sin nombre",
    ocupacionTotal: Number(item.ocupacionTotal ?? item.total ?? 0),
    ocupadas: Number(item.ocupadas ?? item.ocupacionOcupada ?? 0),
    visible: item.visible !== false, // default true
  };
}

function readStoredOcupaciones(storageKey, deposito = "binder1") {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey);
    const arr = raw ? JSON.parse(raw) : [];
    return (arr || []).map((item) => normalizeOcupacion(item, deposito)).filter(Boolean);
  } catch {
    return [];
  }
}

async function apiListOcupaciones(deposito) {
  const params = { deposito, _ts: Date.now() };
  const tries = [
    () => api.get("/api/ocupacion", { params }),
    () => api.get("/api/ocupacion/listar", { params }),
    () => api.get("/api/ocupaciones", { params }),
    () => api.get("/api/ocupaciones/listar", { params }),
  ];

  for (const fn of tries) {
    try {
      const r = await fn();
      const arr = Array.isArray(r.data) ? r.data : r.data?.data || [];
      return arr.map((item) => normalizeOcupacion(item, deposito)).filter(Boolean);
    } catch {}
  }
  throw new Error("No se pudo listar ocupaciones (API).");
}

async function apiCreateOcupacion(payload, deposito) {
  const { data } = await api.post("/api/ocupacion", { ...payload, deposito });
  return normalizeOcupacion(data, deposito);
}

async function apiUpdateOcupacion(id, payload, deposito) {
  const { data } = await api.put(`/api/ocupacion/${id}`, { ...payload, deposito });
  return normalizeOcupacion(data, deposito);
}

// The cache is optional: a storage failure must not turn a confirmed save into an error.
function cacheOcupaciones(storageKey, items) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(items));
  } catch {
    // The server remains the source of truth.
  }
}

const ocupacionErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  "No se pudo guardar la sucursal. Revisá la conexión e intentá nuevamente.";

async function apiDeleteOcupacion(id, deposito) {
  const config = { params: { deposito } };
  const tries = [
    () => api.delete(`/api/ocupacion/${id}`, config),
    () => api.delete(`/api/ocupaciones/${id}`, config),
  ];

  for (const fn of tries) {
    try {
      await fn();
      return;
    } catch {}
  }

  throw new Error("No se pudo eliminar ocupación (API).");
}

/* ================== EXCEL EXPORT ================== */

function safeFileName(s) {
  return String(s || "reporte")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .slice(0, 120);
}

async function loadXLSX() {
  // Preferimos xlsx-js-style (soporta estilos). Si no existe, caemos a xlsx.
  try {
    const mod = await import("xlsx-js-style");
    return { XLSX: mod, styled: true };
  } catch {
    try {
      const mod = await import("xlsx");
      return { XLSX: mod, styled: false };
    } catch {
      return { XLSX: null, styled: false };
    }
  }
}

function makeSheet(XLSX, rows) {
  return XLSX.utils.aoa_to_sheet(rows);
}

function applyHeaderStyle(sheet, XLSX, headerRow = 0, colsCount = 2) {
  // Solo si es xlsx-js-style
  if (!sheet || !sheet["!ref"]) return;
  const r = headerRow;
  for (let c = 0; c < colsCount; c++) {
    const addr = XLSX.utils.encode_cell({ r, c });
    if (!sheet[addr]) continue;
    sheet[addr].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { patternType: "solid", fgColor: { rgb: "0FB872" } },
      alignment: { vertical: "center", horizontal: "center" },
      border: {
        top: { style: "thin", color: { rgb: "132433" } },
        bottom: { style: "thin", color: { rgb: "132433" } },
        left: { style: "thin", color: { rgb: "132433" } },
        right: { style: "thin", color: { rgb: "132433" } },
      },
    };
  }
  // ancho básico
  sheet["!cols"] = new Array(Math.max(2, colsCount)).fill(0).map(() => ({ wch: 22 }));
}

function applyZebra(sheet, XLSX, startRow = 1) {
  if (!sheet || !sheet["!ref"]) return;
  const range = XLSX.utils.decode_range(sheet["!ref"]);
  for (let r = startRow; r <= range.e.r; r++) {
    const zebra = r % 2 === 0;
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!sheet[addr]) continue;
      const prev = sheet[addr].s || {};
      sheet[addr].s = {
        ...prev,
        fill: prev.fill || (zebra ? { patternType: "solid", fgColor: { rgb: "0D1A26" } } : undefined),
        font: prev.font || { color: { rgb: "E9F2FF" } },
        border: prev.border || {
          bottom: { style: "thin", color: { rgb: "132433" } },
        },
      };
    }
  }
}

function applyExcedenteStyle(sheet, XLSX, excedenteCol = 4, startRow = 1) {
  if (!sheet || !sheet["!ref"]) return;
  const range = XLSX.utils.decode_range(sheet["!ref"]);
  for (let r = startRow; r <= range.e.r; r++) {
    const addr = XLSX.utils.encode_cell({ r, c: excedenteCol });
    if (!sheet[addr] || Number(sheet[addr].v || 0) <= 0) continue;
    const prev = sheet[addr].s || {};
    sheet[addr].s = {
      ...prev,
      font: {
        ...(prev.font || {}),
        bold: true,
        color: { rgb: "FF5A67" },
      },
    };
  }
}

function getOcupacionMetrics(item) {
  const totalRaw = Number(item?.ocupacionTotal ?? 0);
  const ocupadasRaw = Number(item?.ocupadas ?? 0);
  const total = Number.isFinite(totalRaw) ? totalRaw : 0;
  const ocupadas = Number.isFinite(ocupadasRaw) ? ocupadasRaw : 0;
  const libres = Math.max(0, total - ocupadas);
  const excedente = Math.max(0, ocupadas - total);
  const pct = total > 0 ? Math.max(0, (ocupadas * 100) / total) : 0;
  return {
    total,
    ocupadas,
    libres,
    excedente,
    pct,
    pctBar: Math.min(100, pct),
  };
}

const normalizeBoardText = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");

const BOARD_LOCATION_GROUPS = [
  { label: "NACIONAL", aliases: ["NACIONAL", "NAC"] },
  { label: "FISCAL", aliases: ["FISCAL", "FIS"] },
  { label: "EA", aliases: ["EA"] },
  { label: "ANEXO CORREO", aliases: ["ANEXO CORREO", "ANEXO", "CORREO"] },
  { label: "SIN UBICACION", aliases: ["SIN UBICACION"] },
];

function getBoardLocationGroup(value = "") {
  const normalized = normalizeBoardText(value);
  if (!normalized) {
    return BOARD_LOCATION_GROUPS[BOARD_LOCATION_GROUPS.length - 1];
  }
  return (
    BOARD_LOCATION_GROUPS.find((group) => group.aliases.includes(normalized)) ||
    null
  );
}

function buildBoardLocationConfig(ocupaciones = []) {
  const exactNames = new Map();
  const namesByGroup = new Map();

  (Array.isArray(ocupaciones) ? ocupaciones : []).forEach((item) => {
    const displayName = String(item?.nombre || "").trim();
    if (!displayName) return;

    exactNames.set(normalizeBoardText(displayName), displayName);

    const group = getBoardLocationGroup(displayName);
    if (group && !namesByGroup.has(group.label)) {
      namesByGroup.set(group.label, displayName);
    }
  });

  return {
    exactNames,
    namesByGroup,
  };
}

function resolveBoardLocationDisplay(value = "", config = null) {
  const normalized = normalizeBoardText(value);
  if (!normalized) {
    return config?.namesByGroup?.get("SIN UBICACION") || "SIN UBICACION";
  }

  if (config?.exactNames?.has(normalized)) {
    return config.exactNames.get(normalized);
  }

  const group = getBoardLocationGroup(normalized);
  if (group && config?.namesByGroup?.has(group.label)) {
    return config.namesByGroup.get(group.label);
  }

  return group?.label || normalized;
}

function getPalletClienteNombre(item) {
  const ingreso = item?.ingreso || {};
  return (
    String(
      ingreso?.clienteNombre ||
        ingreso?.cliente?.nombreCliente ||
        item?.clienteNombre ||
        item?.cliente?.nombreCliente ||
        item?.nombreCliente ||
        "Sin cliente"
    ).trim() || "Sin cliente"
  );
}

function palletCuentaEnOcupacion(item) {
  if (!item || item.cuentaPosicion === false) return false;

  const cantidad = Number(item?.cantidad);
  const egresado = Number(item?.egresado);
  if (Number.isFinite(cantidad) && Number.isFinite(egresado)) {
    return Math.max(0, cantidad - egresado) > 0;
  }

  if (Number.isFinite(egresado)) {
    return egresado <= 0;
  }

  const disponible = Number(item?.disponible);
  if (Number.isFinite(disponible)) {
    return disponible > 0;
  }

  return !item?.fechaEgreso;
}

function getPalletSortTime(item) {
  const raw =
    item?.fechaIngreso ||
    item?.ingreso?.fechaIngreso ||
    item?.createdAt ||
    item?.updatedAt ||
    "";
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getPalletOcupacionLabel(item, boardConfig = null) {
  const raw =
    item?.ubicacionTexto ||
    item?.ingreso?.ubicacionTexto ||
    item?.ubicacion ||
    "";
  return resolveBoardLocationDisplay(raw, boardConfig);
}

function buildOcupacionDetalle(ocupacion, pallets, ocupacionesConfig = []) {
  const metrics = getOcupacionMetrics(ocupacion);
  const boardConfig = buildBoardLocationConfig(ocupacionesConfig);
  const ocupacionKey = normalizeBoardText(
    resolveBoardLocationDisplay(ocupacion?.nombre, boardConfig)
  );
  const posicionesActivas = [];
  const clientesMap = new Map();

  (Array.isArray(pallets) ? pallets : []).forEach((item) => {
    const ubicacionKey = normalizeBoardText(
      getPalletOcupacionLabel(item, boardConfig)
    );
    if (ubicacionKey !== ocupacionKey) return;
    if (!palletCuentaEnOcupacion(item)) return;

    const cliente = getPalletClienteNombre(item);
    posicionesActivas.push({ cliente, sortTime: getPalletSortTime(item) });

    const current = clientesMap.get(cliente) || {
      cliente,
      posiciones: 0,
      excedente: 0,
    };
    current.posiciones += 1;
    clientesMap.set(cliente, current);
  });

  const excedenteAsignado = Math.min(metrics.excedente, posicionesActivas.length);
  posicionesActivas
    .slice()
    .sort((a, b) => b.sortTime - a.sortTime)
    .slice(0, excedenteAsignado)
    .forEach((item) => {
      const current = clientesMap.get(item.cliente);
      if (current) {
        current.excedente += 1;
      }
    });

  const clientes = Array.from(clientesMap.values()).sort((a, b) => {
    if (b.posiciones !== a.posiciones) return b.posiciones - a.posiciones;
    if (b.excedente !== a.excedente) return b.excedente - a.excedente;
    return a.cliente.localeCompare(b.cliente, "es", { sensitivity: "base" });
  });

  return {
    clientes,
    posicionesDetectadas: posicionesActivas.length,
    excedenteAsignado,
  };
}

/* ================== PRINCIPAL ================== */

export default function InicioOperaciones() {
  useDashStyles();
  const location = useLocation();
  const initialSelectedDate = useMemo(() => fechaLocalYMD(), []);
  const { deposito, setDeposito } = useUserId();
  const depositoFromRoute = readDepositoFromSearch(location.search);
  const depositoActual = resolveDepositoRoute({
    search: location.search,
    fallback: deposito,
  });
  const [ocupacionDeposito, setOcupacionDeposito] = useState(() =>
    normalizeOcupacionDeposito(depositoActual)
  );
  const ocupacionesStorageKey = useMemo(
    () => buildOcupacionesStorageKey(ocupacionDeposito),
    [ocupacionDeposito]
  );
  const ocuRequestRef = useRef(0);

  const [loading, setLoading] = useState(false);

  // Fecha controlable para TODO
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [flowDateFrom, setFlowDateFrom] = useState(() =>
    addDaysUTC(initialSelectedDate, -(DEFAULT_FLOW_RANGE_DAYS - 1))
  );
  const [stats, setStats] = useState(null); // { fiscal1, fiscal2, nacional1, nacional2, global }
  const [serie, setSerie] = useState([]); // [{fecha, label, fiscal1,fiscal2,nacional1,nacional2}]
  const [truckSummary, setTruckSummary] = useState(null); // resumen camiones día

  const [ocuLoading, setOcuLoading] = useState(false);
  const [ocupaciones, setOcupaciones] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [ocuSaving, setOcuSaving] = useState(false);
  const [ocuSaveError, setOcuSaveError] = useState("");
  const ocuSavingRef = useRef(false);
  const [detalleOcupacionOpen, setDetalleOcupacionOpen] = useState(false);
  const [detalleOcupacion, setDetalleOcupacion] = useState(null);
  const [detalleOcupacionData, setDetalleOcupacionData] = useState(null);
  const [detalleOcupacionLoading, setDetalleOcupacionLoading] = useState(false);
  const [detalleOcupacionError, setDetalleOcupacionError] = useState("");
  const [editing, setEditing] = useState(null); // ocupacion o null
  const [form, setForm] = useState({
    deposito: normalizeOcupacionDeposito(depositoActual),
    nombre: "",
    ocupacionTotal: 0,
  });
  const detalleOcupacionRequestRef = useRef(0);

  const ocupacionesById = useMemo(() => {
    const map = new Map();
    (ocupaciones || []).forEach((o) => {
      if (!o) return;
      const id = o._id != null ? String(o._id) : "";
      if (!id) return;
      map.set(id, (o.nombre || "").trim());
    });
    return map;
  }, [ocupaciones]);

  const resolveOcupacionNombre = (op) => {
    if (!op) return "";
    const nombre = (op.ocupacionNombre || "").trim();
    if (nombre) return nombre;
    const id = op.ocupacionId != null ? String(op.ocupacionId) : "";
    if (!id) return "";
    return (ocupacionesById.get(id) || "").trim();
  };

  // === Carga ops: por defecto trae los últimos 7 días hasta selectedDate ===
  // Usa la fecha "Hasta" como referencia del resumen y del rango visible.
  const flowDateTo = selectedDate;
  const safeFlowDateFrom = useMemo(() => {
    if (!flowDateFrom) return addDaysUTC(flowDateTo, -(DEFAULT_FLOW_RANGE_DAYS - 1));
    return flowDateFrom > flowDateTo ? flowDateTo : flowDateFrom;
  }, [flowDateFrom, flowDateTo]);
  const flowRangeLabel = useMemo(
    () => `${safeFlowDateFrom} a ${flowDateTo}`,
    [safeFlowDateFrom, flowDateTo]
  );

  const handleDateToChange = (nextDate) => {
    if (!nextDate) return;
    setSelectedDate(nextDate);
    setFlowDateFrom((currentFrom) => {
      if (!currentFrom) return nextDate;
      return currentFrom <= nextDate ? currentFrom : nextDate;
    });
  };

  const handleDateFromChange = (nextDate) => {
    if (!nextDate) return;
    setFlowDateFrom(nextDate);
    if (nextDate > flowDateTo) {
      setSelectedDate(nextDate);
    }
  };

  useEffect(() => {
    if (depositoFromRoute && depositoFromRoute !== deposito) {
      setDeposito(depositoFromRoute);
    }
  }, [depositoFromRoute, deposito, setDeposito]);

  useEffect(() => {
    setOcupacionDeposito(normalizeOcupacionDeposito(depositoActual));
  }, [depositoActual]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const hastaStr = flowDateTo;
      const desdeStr = safeFlowDateFrom;
      const cacheBuster = Date.now();

      const [resp1, resp2] = await Promise.all([
        api.get("/api/operaciones/listar-rango", {
          params: { desde: desdeStr, hasta: hastaStr, deposito: "binder1", _ts: cacheBuster },
        }),
        api.get("/api/operaciones/listar-rango", {
          params: { desde: desdeStr, hasta: hastaStr, deposito: "binder2", _ts: cacheBuster },
        }),
      ]);

      const docs = []
        .concat(Array.isArray(resp1.data) ? resp1.data : [])
        .concat(Array.isArray(resp2.data) ? resp2.data : []);

      procesarDatos(docs, desdeStr, hastaStr);
    } catch (e) {
      console.error("Error cargando datos de operaciones", e);
      setStats(null);
      setSerie([]);
      setTruckSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const cargarOcupaciones = async () => {
    if (ocuSavingRef.current) return;
    const requestId = ++ocuRequestRef.current;
    const depositoTarget = normalizeOcupacionDeposito(ocupacionDeposito);
    const storageKey = buildOcupacionesStorageKey(depositoTarget);
    setOcuLoading(true);
    try {
      const arr = await apiListOcupaciones(depositoTarget);
      if (ocuRequestRef.current !== requestId) return;
      setOcupaciones(arr);
      cacheOcupaciones(storageKey, arr);
    } catch (e) {
      if (ocuRequestRef.current !== requestId) return;
      setOcupaciones(readStoredOcupaciones(storageKey, depositoTarget));
    } finally {
      if (ocuRequestRef.current === requestId) {
        setOcuLoading(false);
      }
    }
  };

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, safeFlowDateFrom]);

  useEffect(() => {
    setOcupaciones(readStoredOcupaciones(ocupacionesStorageKey, ocupacionDeposito));
  }, [ocupacionesStorageKey, ocupacionDeposito]);

  useEffect(() => {
    cargarOcupaciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocupacionDeposito, ocupacionesStorageKey]);

  const procesarDatos = (docs, desdeStr, hastaStr) => {
    const { regularDocs, dapCounts } = separateDashboardDap(docs, hastaStr, {
      countTrucks: inferTruckCount,
      groupFor: classifyGroup,
    });
    const emptyGroup = () => ({
      total: 0,
      cerrados: 0,
      ingresos: 0,
      egresos: 0,
      comentarios: 0,
    });

    const groups = {
      fiscal1: emptyGroup(),
      fiscal2: emptyGroup(),
      nacional1: emptyGroup(),
      nacional2: emptyGroup(),
    };
    Object.keys(groups).forEach((key) => {
      groups[key].dapIngresos = dapCounts[key].ingresos;
      groups[key].dapEgresos = dapCounts[key].egresos;
    });

    const resumenDia = {
      total: 0,
      pendientes: 0,
      proceso: 0,
      finalizados: 0,
      comentarios: 0,
      // extra global para meter en tabla
      globalPct: 0,
    };

    // Serie semanal (4 líneas)
    const serieMap = {};
    buildDateRangeUTC(desdeStr, hastaStr).forEach((iso) => {
      serieMap[iso] = {
        fecha: iso,
        label: fmtLabel(iso),
        fiscal1: 0,
        fiscal2: 0,
        nacional1: 0,
        nacional2: 0,
        nacional1Ocu: new Set(),
        nacional2Ocu: new Set(),
      };
    });

    regularDocs.forEach((doc) => {
      const docFechaIso =
        typeof doc.fecha === "string" ? doc.fecha.slice(0, 10) : fechaUTC(doc.fecha || new Date());
      const dep = doc.deposito || "binder1";
      const daySerie = serieMap[docFechaIso];
      const ops = Array.isArray(doc.Operacion) ? doc.Operacion : [];
      const esDiaSeleccionado = docFechaIso === hastaStr;

      ops.forEach((op) => {
        const esOperacionEspejo =
          !!op.origenOperacionId ||
          !!op.origenFecha ||
          !!op.pospuestoDe ||
          !!op.noContarEnGraficos;

        if (esOperacionEspejo) {
          return;
        }

        const gKey = classifyGroup(op, dep);
        const grupo = gKey ? groups[gKey] : null;
        const cantCamiones = inferTruckCount(op);
        const tieneComentario = hasOperacionComentario(op);
        const esComentarioExclusivo = isCommentOnlyOperation(op);

        if (tieneComentario) {
          if (esDiaSeleccionado && grupo) grupo.comentarios += 1;
          if (esDiaSeleccionado) resumenDia.comentarios += 1;
        }

        if (esComentarioExclusivo) {
          return;
        }

        if (cantCamiones === 0) return;

        if (daySerie && (gKey === "nacional1" || gKey === "nacional2")) {
          const nombreOcu = resolveOcupacionNombre(op) || "Sin asignar";
          const ocuKey = gKey === "nacional1" ? "nacional1Ocu" : "nacional2Ocu";
          const bucket = daySerie[ocuKey];
          if (bucket) bucket.add(nombreOcu);
        }

        for (let i = 0; i < cantCamiones; i++) {
          const estado = getEstadoCamion(op, i);
          const esFinal = estado === "FINALIZADO";

          // Serie (por día) -> cuenta camiones por grupo
          if (daySerie && gKey && daySerie[gKey] !== undefined) {
            daySerie[gKey] += 1;
          }

          // Tarjetas y resumen SOLO del día seleccionado
          if (esDiaSeleccionado) {
            // tarjeta grupo
            if (grupo) {
              grupo.total += 1;
              if (esFinal) grupo.cerrados += 1;
              if (op.tipo === "Ingreso") grupo.ingresos += 1;
              else if (op.tipo === "Egreso") grupo.egresos += 1;
            }

            // resumen global
            resumenDia.total += 1;
            if (estado === "FINALIZADO") resumenDia.finalizados += 1;
            else if (estado === "PENDIENTE") resumenDia.pendientes += 1;
            else resumenDia.proceso += 1;
          }
        }
      });

      const observaciones = Array.isArray(doc.observaciones_anotaciones)
        ? doc.observaciones_anotaciones
        : [];

      observaciones.forEach((obs) => {
        if (!esDiaSeleccionado || !hasNonEmptyText(obs?.observacion)) return;

        const obsGroupKey = classifyObservacionGroup(obs, dep);
        if (obsGroupKey) groups[obsGroupKey].comentarios += 1;
        resumenDia.comentarios += 1;
      });
    });

    const globalTotal =
      groups.fiscal1.total + groups.fiscal2.total + groups.nacional1.total + groups.nacional2.total;
    const globalCerrados =
      groups.fiscal1.cerrados + groups.fiscal2.cerrados + groups.nacional1.cerrados + groups.nacional2.cerrados;
    const globalComentarios = resumenDia.comentarios;

    const globalPct = globalTotal > 0 ? (globalCerrados * 100) / globalTotal : 0;
    resumenDia.globalPct = globalPct;

    setStats({
      ...groups,
      global: { total: globalTotal, cerrados: globalCerrados, comentarios: globalComentarios, pct: globalPct },
    });

    const serieArr = Object.values(serieMap)
      .sort((a, b) => a.fecha.localeCompare(b.fecha))
      .map((row) => ({
        ...row,
        nacional1Ocu: Array.from(row.nacional1Ocu || []),
        nacional2Ocu: Array.from(row.nacional2Ocu || []),
      }));
    setSerie(serieArr);
    setTruckSummary(resumenDia);
  };

  /* ===== Ocupaciones UI ===== */

  const abrirCrear = () => {
    setOcuSaveError("");
    setEditing(null);
    setForm({
      deposito: ocupacionDeposito,
      nombre: "",
      ocupacionTotal: 0,
    });
    setModalOpen(true);
  };

  const abrirEditar = (ocu) => {
    setOcuSaveError("");
    setEditing(ocu);
    setForm({
      deposito: normalizeOcupacionDeposito(ocu?.deposito || ocupacionDeposito),
      nombre: ocu?.nombre || "",
      ocupacionTotal: ocu?.ocupacionTotal || 0,
    });
    setModalOpen(true);
  };

  const cerrarDetalleOcupacion = () => {
    detalleOcupacionRequestRef.current += 1;
    setDetalleOcupacionOpen(false);
    setDetalleOcupacion(null);
    setDetalleOcupacionData(null);
    setDetalleOcupacionLoading(false);
    setDetalleOcupacionError("");
  };

  const removeOcupacionLocally = (ocu, depositoDestino) => {
    const targetStorageKey = buildOcupacionesStorageKey(depositoDestino);
    const currentRows =
      depositoDestino === ocupacionDeposito
        ? ocupaciones
        : readStoredOcupaciones(targetStorageKey, depositoDestino);
    const next = currentRows.filter((item) => item && item._id !== ocu._id);

    cacheOcupaciones(targetStorageKey, next);

    if (depositoDestino === ocupacionDeposito) {
      setOcupaciones(next);
    }

    if (detalleOcupacion?._id === ocu._id) {
      cerrarDetalleOcupacion();
    }

    if (editing?._id === ocu._id) {
      setEditing(null);
      setModalOpen(false);
    }
  };

  const abrirDetalleOcupacion = async (ocu) => {
    const requestId = ++detalleOcupacionRequestRef.current;
    const depositoTarget = normalizeOcupacionDeposito(
      ocu?.deposito || ocupacionDeposito
    );

    setDetalleOcupacionOpen(true);
    setDetalleOcupacion(ocu);
    setDetalleOcupacionData(null);
    setDetalleOcupacionLoading(true);
    setDetalleOcupacionError("");

    try {
      const { data } = await api.get("/api/pallets", {
        params: {
          deposito: depositoTarget,
          page: 1,
          limit: 5000,
          sort: "-fechaIngreso",
          _ts: Date.now(),
        },
      });

      if (detalleOcupacionRequestRef.current !== requestId) return;

      const items = Array.isArray(data?.data?.items) ? data.data.items : [];
      setDetalleOcupacionData(buildOcupacionDetalle(ocu, items, ocupaciones));
    } catch (error) {
      if (detalleOcupacionRequestRef.current !== requestId) return;
      console.error("GET /api/pallets", error?.response || error);
      setDetalleOcupacionError(
        "No se pudo cargar el detalle por cliente para esta sucursal."
      );
    } finally {
      if (detalleOcupacionRequestRef.current === requestId) {
        setDetalleOcupacionLoading(false);
      }
    }
  };

  const guardarOcupacion = async () => {
    if (ocuSavingRef.current) return;
    const depositoDestino = normalizeOcupacionDeposito(
      form.deposito || editing?.deposito || ocupacionDeposito
    );
    const targetStorageKey = buildOcupacionesStorageKey(depositoDestino);
    const nombre = (form.nombre || "").trim();
    const ocupacionTotal = Number(form.ocupacionTotal || 0);

    if (!nombre) {
      setOcuSaveError("Ingresá el nombre de la sucursal.");
      return;
    }
    if (!Number.isFinite(ocupacionTotal) || ocupacionTotal < 0) {
      setOcuSaveError("La ocupación total debe ser un número mayor o igual a cero.");
      return;
    }

    ocuSavingRef.current = true;
    setOcuSaving(true);
    setOcuSaveError("");
    // Ignore any list request started before this mutation.
    ocuRequestRef.current += 1;
    setOcuLoading(false);
    try {
      if (!editing) {
        const created = await apiCreateOcupacion(
          { nombre, ocupacionTotal },
          depositoDestino
        );
        const currentRows =
          depositoDestino === ocupacionDeposito
            ? ocupaciones
            : readStoredOcupaciones(targetStorageKey, depositoDestino);
        const next = [...currentRows, normalizeOcupacion(created, depositoDestino)].filter(Boolean);
        cacheOcupaciones(targetStorageKey, next);
        if (depositoDestino === ocupacionDeposito) {
          setOcupaciones(next);
        } else {
          setOcupacionDeposito(depositoDestino);
        }
      } else {
        const updated = await apiUpdateOcupacion(
          editing._id,
          {
            nombre,
            ocupacionTotal,
            visible: editing.visible !== false,
          },
          depositoDestino
        );
        const next = ocupaciones.map((x) =>
          x._id === editing._id ? { ...x, ...normalizeOcupacion(updated, depositoDestino) } : x
        );
        setOcupaciones(next);
        cacheOcupaciones(targetStorageKey, next);
      }
      notifySuccess(editing ? "Sucursal actualizada correctamente." : "Sucursal creada correctamente.");
      setModalOpen(false);
    } catch (error) {
      setOcuSaveError(ocupacionErrorMessage(error));
    } finally {
      ocuSavingRef.current = false;
      setOcuSaving(false);
    }
  };

  const toggleVisible = async (ocu) => {
    const depositoDestino = normalizeOcupacionDeposito(ocu?.deposito || ocupacionDeposito);
    const targetStorageKey = buildOcupacionesStorageKey(depositoDestino);
    const newVisible = !(ocu.visible !== false);
    try {
      const updated = await apiUpdateOcupacion(ocu._id, { visible: newVisible }, depositoDestino);
      setOcupaciones((current) => {
        const next = current.map((x) => x._id === ocu._id ? { ...x, ...updated } : x);
        cacheOcupaciones(targetStorageKey, next);
        return next;
      });
      notifySuccess(newVisible ? "Sucursal visible nuevamente." : "Sucursal ocultada.");
    } catch (error) {
      window.alert(ocupacionErrorMessage(error));
    }
  };

  const eliminarOcupacion = async (ocu) => {
    const depositoDestino = normalizeOcupacionDeposito(
      ocu?.deposito || ocupacionDeposito
    );
    const nombre = String(ocu?.nombre || "esta sucursal").trim() || "esta sucursal";
    const ocupadas = Number(ocu?.ocupadas || 0);
    const confirmMessage =
      ocupadas > 0
        ? `Eliminar la sucursal "${nombre}"? Tiene ${ocupadas} posiciones ocupadas. Esta accion no se puede deshacer.`
        : `Eliminar la sucursal "${nombre}"? Esta accion no se puede deshacer.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const isLocalOnly = String(ocu?._id || "").startsWith("ls_");
      if (!isLocalOnly) {
        await apiDeleteOcupacion(ocu._id, depositoDestino);
      }

      removeOcupacionLocally(ocu, depositoDestino);
      notifySuccess(isLocalOnly ? "Sucursal eliminada de este equipo." : "Sucursal eliminada correctamente.");
    } catch (error) {
      console.error("DELETE /api/ocupacion", error?.response || error);
      window.alert("No se pudo eliminar la sucursal.");
    }
  };

  const ocupacionesVisibles = useMemo(() => (ocupaciones || []).filter((o) => o && o.visible !== false), [ocupaciones]);
  const ocupacionesOcultas = useMemo(() => (ocupaciones || []).filter((o) => o && o.visible === false), [ocupaciones]);
  const detalleOcupacionMetrics = useMemo(
    () => getOcupacionMetrics(detalleOcupacion),
    [detalleOcupacion]
  );

  /* ===== Leyenda líneas ===== */
  const legend = [
    { name: "Fiscal Binder 1", key: "fiscal1", color: "#f59e0b" },
    { name: "Fiscal Binder 2", key: "fiscal2", color: "#ef4444" },
    { name: "Nacional Binder 1", key: "nacional1", color: "#38bdf8" },
    { name: "Nacional Binder 2", key: "nacional2", color: "#22c55e" },
  ];
  const mostrarGraficosOperativos = true;

  /* ===== Export ===== */
  const exportarExcel = async () => {
    const { XLSX, styled } = await loadXLSX();
    const fecha = selectedDate;

    // Si no hay XLSX, export CSV simple
    if (!XLSX) {
      const csvLines = [];
      csvLines.push(["Reporte Operaciones", fecha].join(","));
      csvLines.push(["Camiones total", truckSummary?.total ?? 0].join(","));
      csvLines.push(["Pendientes", truckSummary?.pendientes ?? 0].join(","));
      csvLines.push(["En proceso", truckSummary?.proceso ?? 0].join(","));
      csvLines.push(["Finalizados", truckSummary?.finalizados ?? 0].join(","));
      csvLines.push(["Comentarios", truckSummary?.comentarios ?? 0].join(","));
      csvLines.push("");
      csvLines.push(["Fecha", "Fiscal1", "Fiscal2", "Nacional1", "Nacional2"].join(","));
      (serie || []).forEach((r) => {
        csvLines.push([r.fecha, r.fiscal1, r.fiscal2, r.nacional1, r.nacional2].join(","));
      });

      const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = safeFileName(`reporte_operaciones_${fecha}.csv`);
      a.click();
      URL.revokeObjectURL(url);
      return;
    }

    const wb = XLSX.utils.book_new();

    // Hoja 1: Resumen día
    const resumenRows = [
      ["Resumen del día", fecha],
      [],
      ["Indicador", "Valor"],
      ["Camiones (total)", truckSummary?.total ?? 0],
      ["Camiones pendientes", truckSummary?.pendientes ?? 0],
      ["Camiones en proceso", truckSummary?.proceso ?? 0],
      ["Camiones finalizados", truckSummary?.finalizados ?? 0],
      ["% global cerrados", `${(truckSummary?.globalPct ?? 0).toFixed(1)}%`],
      ["Observaciones/Anotaciones", truckSummary?.comentarios ?? 0],
    ];
    const wsResumen = makeSheet(XLSX, resumenRows);
    wsResumen["!cols"] = [{ wch: 34 }, { wch: 22 }];
    if (styled) {
      applyHeaderStyle(wsResumen, XLSX, 2, 2);
      applyZebra(wsResumen, XLSX, 3);
    }
    XLSX.utils.book_append_sheet(wb, wsResumen, "ResumenDia");

    // Hoja 2: Tarjetas
    const tarjetasRows = [
      ["Tarjetas (día seleccionado)", fecha],
      [],
      ["Grupo", "Total camiones", "Cerrados", "Ingresos", "Egresos", "Comentarios"],
      ["Fiscal 1", stats?.fiscal1?.total ?? 0, stats?.fiscal1?.cerrados ?? 0, stats?.fiscal1?.ingresos ?? 0, stats?.fiscal1?.egresos ?? 0, stats?.fiscal1?.comentarios ?? 0],
      ["Fiscal 2", stats?.fiscal2?.total ?? 0, stats?.fiscal2?.cerrados ?? 0, stats?.fiscal2?.ingresos ?? 0, stats?.fiscal2?.egresos ?? 0, stats?.fiscal2?.comentarios ?? 0],
      ["Nacional 1", stats?.nacional1?.total ?? 0, stats?.nacional1?.cerrados ?? 0, stats?.nacional1?.ingresos ?? 0, stats?.nacional1?.egresos ?? 0, stats?.nacional1?.comentarios ?? 0],
      ["Nacional 2", stats?.nacional2?.total ?? 0, stats?.nacional2?.cerrados ?? 0, stats?.nacional2?.ingresos ?? 0, stats?.nacional2?.egresos ?? 0, stats?.nacional2?.comentarios ?? 0],
      ["GLOBAL", stats?.global?.total ?? 0, stats?.global?.cerrados ?? 0, "", "", stats?.global?.comentarios ?? 0],
    ];
    const wsTarjetas = makeSheet(XLSX, tarjetasRows);
    wsTarjetas["!cols"] = [{ wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 14 }];
    if (styled) {
      applyHeaderStyle(wsTarjetas, XLSX, 2, 6);
      applyZebra(wsTarjetas, XLSX, 3);
    }
    XLSX.utils.book_append_sheet(wb, wsTarjetas, "Tarjetas");

    // Hoja 3: Flujo semanal
    const flujoRows = [
      ["Flujo operativo semanal (últimos 7 días)", `${addDaysUTC(selectedDate, -6)} a ${selectedDate}`],
      [],
      ["Fecha", "Fiscal 1", "Fiscal 2", "Nacional 1", "Nacional 2"],
      ...(serie || []).map((r) => [r.fecha, r.fiscal1 ?? 0, r.fiscal2 ?? 0, r.nacional1 ?? 0, r.nacional2 ?? 0]),
    ];
    flujoRows[0] = ["Flujo operativo", flowRangeLabel];
    const wsFlujo = makeSheet(XLSX, flujoRows);
    wsFlujo["!cols"] = [{ wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }];
    if (styled) {
      applyHeaderStyle(wsFlujo, XLSX, 2, 5);
      applyZebra(wsFlujo, XLSX, 3);
    }
    XLSX.utils.book_append_sheet(wb, wsFlujo, "FlujoOperativo");

    // Hoja 4: Ocupación
    const ocuRows = [
      ["Factor ocupación (sucursales)", binderLabel(ocupacionDeposito)],
      [],
      ["Sucursal", "Capacidad total", "Ocupadas", "Libres", "Excedente", "% ocupación", "Visible"],
      ...(ocupaciones || []).map((o) => {
        const { total, ocupadas, libres, excedente, pct } = getOcupacionMetrics(o);
        return [o.nombre, total, ocupadas, libres, excedente, `${pct.toFixed(0)}%`, o.visible !== false ? "Sí" : "No"];
      }),
    ];
    const wsOcu = makeSheet(XLSX, ocuRows);
    wsOcu["!cols"] = [{ wch: 20 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 10 }];
    if (styled) {
      applyHeaderStyle(wsOcu, XLSX, 2, 7);
      applyZebra(wsOcu, XLSX, 3);
      applyExcedenteStyle(wsOcu, XLSX, 4, 3);
    }
    XLSX.utils.book_append_sheet(wb, wsOcu, "Ocupacion");

    // Export
    const file = safeFileName(`reporte_operaciones_${fecha}.xlsx`);
    XLSX.writeFile(wb, file);
    notifySuccess("Reporte de operaciones generado correctamente.");
  };

  return (
    <div className="dashboard-wrap">
      <div className="dash-header">
        <div className="dash-title">
          <h1>Status del día</h1>
        </div>

        <div className="dash-actions">
          <div className="date-pill" title="Cambiar fecha para ver los gráficos y tarjetas">
            <label>
              <FiCalendar /> Fecha
            </label>
            <input
              type="date"
              value={selectedDate} // Conecta con el estado
              onChange={(e) => handleDateToChange(e.target.value)} // Actualiza el estado al cambiar
            />
          </div>

          <button className="dash-btn" onClick={cargarDatos} disabled={loading}>
            <FiRefreshCw className={loading ? "spin" : ""} />
            <span>{loading ? "Cargando..." : "Actualizar"}</span>
          </button>
        </div>
      </div>



      {stats && (
        <div className="section-block">
          <div className="cards-row">
            <StatusCard data={stats?.fiscal1} title="Fiscal Binder 1" />
            <StatusCard data={stats?.fiscal2} title="Fiscal Binder 2" />
            <StatusCard data={stats?.nacional1} title="Nacional Binder 1" />
            <StatusCard data={stats?.nacional2} title="Nacional Binder 2" />
          </div>
        </div>
      )}

      {/* ✅ Factor ocupación */}
      <div className="section-block">
        <h2 className="section-title">Factor Ocupación</h2>
        <div className="graph-panel occupation-panel">
          <div className="ocu-topbar ocu-topbar--actions">
            <div className="ocu-topbar-copy">
              <div
                className="ocu-segment"
                role="tablist"
                aria-label="Seleccionar binder para factor de ocupación"
              >
                {["binder1", "binder2"].map((dep) => (
                  <button
                    key={dep}
                    type="button"
                    className={`ocu-segment-btn${ocupacionDeposito === dep ? " active" : ""}`}
                    aria-pressed={ocupacionDeposito === dep}
                    onClick={() => setOcupacionDeposito(dep)}
                  >
                    {binderLabel(dep)}
                  </button>
                ))}
              </div>
              <div className="ocu-topbar-note">
                Se muestran las sucursales y la ocupación cargadas para {binderLabel(ocupacionDeposito)}.
              </div>
            </div>
            <div className="ocu-actions">
              <button className="dash-btn" onClick={cargarOcupaciones} disabled={ocuLoading}>
                <FiRefreshCw /> Actualizar
              </button>
              <button className="dash-btn" onClick={abrirCrear}>
                <FiPlus /> Crear sucursal
              </button>
            </div>
          </div>

        {ocuLoading && (
          <div style={{ fontSize: 12, color: "var(--muted)" }}>Cargando ocupaciones…</div>
        )}

        <div className="ocu-grid" style={{ marginTop: 10 }}>
          {ocupacionesVisibles.map((ocu) => {
            const { total, ocupadas, libres, excedente, pct, pctBar } = getOcupacionMetrics(ocu);

            return (
              <div
                key={ocu._id}
                className="ocu-card actionable"
                role="button"
                tabIndex={0}
                onClick={() => abrirDetalleOcupacion(ocu)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    abrirDetalleOcupacion(ocu);
                  }
                }}
                title="Ver detalle por cliente"
              >
                <div className="ocu-card-header">
                  <div className="ocu-title">{ocu.nombre}</div>
                <div className="ocu-tools">
                  <button
                    className="icon-btn"
                    type="button"
                    title="Editar"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirEditar(ocu);
                    }}
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="icon-btn"
                    type="button"
                    title="Ocultar"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleVisible(ocu);
                    }}
                  >
                    <FiEyeOff />
                  </button>
                  <button
                    className="icon-btn"
                    type="button"
                    title="Eliminar"
                    style={{ color: "var(--danger)" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      eliminarOcupacion(ocu);
                    }}
                  >
                    <FiTrash2 />
                  </button>
                </div>

                </div>
                <div className="ocu-sub">Capacidad total: <strong>{total}</strong></div>

                <div className="ocu-metrics">
                  <div>
                    <div className="ocu-pct">{pct.toFixed(0)}%</div>
                    <div className="ocu-sub">ocupación</div>
                  </div>
                  <div className="ocu-mini">
                    <div>Ocupadas: <strong style={{ color: "var(--text)" }}>{ocupadas}</strong></div>
                    <div>Libres: <strong style={{ color: "var(--text)" }}>{libres}</strong></div>
                    <div>Excedente: <strong style={{ color: excedente > 0 ? "var(--danger)" : "var(--text)" }}>{excedente}</strong></div>
                  </div>
                </div>

                <div className="ocu-bar" aria-hidden="true">
                  <div style={{ width: `${pctBar}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        {!ocuLoading && ocupacionesVisibles.length === 0 && ocupacionesOcultas.length === 0 && (
          <div className="ocu-empty">
            No hay sucursales cargadas para {binderLabel(ocupacionDeposito)}.
          </div>
        )}

        {/* Ocultas (opcional para re-mostrar) */}
        {ocupacionesOcultas.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>
              Ocultas (click para volver a ver):
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {ocupacionesOcultas.map((ocu) => (
                <React.Fragment key={ocu._id}>
                  <button className="btn" type="button" onClick={() => toggleVisible(ocu)} title="Mostrar">
                    <FiEye /> {ocu.nombre}
                  </button>
                  <button
                    className="btn"
                    type="button"
                    onClick={() => eliminarOcupacion(ocu)}
                    title={`Eliminar ${ocu.nombre}`}
                    style={{ borderColor: "rgba(255,90,103,.35)", color: "var(--danger)" }}
                  >
                    <FiTrash2 /> Eliminar
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Resumen día */}
      {mostrarGraficosOperativos && (
        <div className="section-block">
          <div className="section-head">
            <h2 className="section-title">Flujo operativo</h2>
            <div className="section-range-controls">
              <div className="range-date-pill">
                <label>
                  <FiCalendar /> Desde
                </label>
                <input
                  type="date"
                  value={safeFlowDateFrom}
                  onChange={(e) => handleDateFromChange(e.target.value)}
                />
              </div>

              <div className="range-date-pill">
                <label>
                  <FiCalendar /> Hasta
                </label>
                <input
                  type="date"
                  value={flowDateTo}
                  onChange={(e) => handleDateToChange(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="graph-panel">
            <div className="graph-header graph-header--legend">
              <div className="graph-legend">
                {legend.map((l) => (
                  <span className="legend-pill" key={l.name}>
                    <span className="legend-dot" style={{ background: l.color }} />
                    {l.name}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={serie}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="label" stroke="#9ca3af" fontSize={11} />
                  <YAxis allowDecimals={false} stroke="#9ca3af" fontSize={11} width={26} />
                  <Tooltip content={<CustomTooltip4 />} />

                  {legend.map((item) => (
                    <Line
                      key={item.key}
                      type="monotone"
                      dataKey={item.key}
                      name={item.name}
                      stroke={item.color}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {truckSummary && (
        <div className="section-block">
          <h2 className="section-title">Resumen de operaciones del d&iacute;a</h2>
          <div className="summary-layout">
            <div className="graph-panel summary-panel">
              <table className="truck-table">
                <thead>
                  <tr>
                    <th>Indicador</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Camiones (total)</td>
                    <td><strong>{truckSummary.total}</strong></td>
                  </tr>
                  <tr>
                    <td>Camiones pendientes</td>
                    <td><strong>{truckSummary.pendientes}</strong></td>
                  </tr>
                  <tr>
                    <td>Camiones en proceso</td>
                    <td><strong>{truckSummary.proceso}</strong></td>
                  </tr>
                  <tr>
                    <td>Camiones finalizados</td>
                    <td><strong>{truckSummary.finalizados}</strong></td>
                  </tr>
                  <tr>
                    <td>% global cerrados</td>
                    <td><strong>{(truckSummary.globalPct || 0).toFixed(1)}%</strong></td>
                  </tr>
                  <tr>
                    <td>Comentarios (todos los binders)</td>
                    <td><strong>{truckSummary.comentarios}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {false && (
        <div className="section-block">
          <h2 className="section-title">Flujo operativo</h2>
          <div className="graph-panel">
            <div className="graph-header">
              <div className="graph-legend">
                {legend.map((l) => (
                  <span className="legend-pill" key={l.name}>
                    <span className="legend-dot" style={{ background: l.color }} />
                    {l.name}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={serie}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="label" stroke="#9ca3af" fontSize={11} />
                  <YAxis allowDecimals={false} stroke="#9ca3af" fontSize={11} width={26} />
                  <Tooltip content={<CustomTooltip4 />} />

                  {legend.map((item) => (
                    <Line
                      key={item.key}
                      type="monotone"
                      dataKey={item.key}
                      name={item.name}
                      stroke={item.color}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }}>
          Cargando datos…
        </div>
      )}

      {mostrarGraficosOperativos && (
        <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
          <button className="dash-btn" onClick={exportarExcel} title="Exporta resumen, tarjetas, flujo semanal y ocupación">
            <FiDownload /> Exportar a Excel
          </button>
        </div>
      )}

      <Modal
        open={detalleOcupacionOpen}
        title={
          detalleOcupacion
            ? `Detalle de ocupación · ${detalleOcupacion.nombre}`
            : "Detalle de ocupación"
        }
        onClose={cerrarDetalleOcupacion}
        width="min(780px, 96vw)"
        footer={
          <button className="btn" type="button" onClick={cerrarDetalleOcupacion}>
            <FiX /> Cerrar
          </button>
        }
      >
        {detalleOcupacion && (
          <>
            <div className="ocu-detail-summary">
              <div className="ocu-detail-kpi">
                <span>Capacidad</span>
                <strong>{detalleOcupacionMetrics.total}</strong>
              </div>
              <div className="ocu-detail-kpi">
                <span>Ocupadas</span>
                <strong>{detalleOcupacionMetrics.ocupadas}</strong>
              </div>
              <div className="ocu-detail-kpi">
                <span>Libres</span>
                <strong>{detalleOcupacionMetrics.libres}</strong>
              </div>
              <div className="ocu-detail-kpi">
                <span>Excedente</span>
                <strong
                  style={{
                    color:
                      detalleOcupacionMetrics.excedente > 0
                        ? "var(--danger)"
                        : "var(--text)",
                  }}
                >
                  {detalleOcupacionMetrics.excedente}
                </strong>
              </div>
            </div>

            {detalleOcupacionMetrics.excedente > 0 && (
              <div className="ocu-detail-note">
                El excedente se reparte sobre las posiciones más recientes de la sucursal para identificar a qué clientes afecta.
              </div>
            )}

            {detalleOcupacionLoading && (
              <div className="ocu-detail-state">Cargando detalle por cliente…</div>
            )}

            {!detalleOcupacionLoading && detalleOcupacionError && (
              <div className="ocu-detail-state error">{detalleOcupacionError}</div>
            )}

            {!detalleOcupacionLoading &&
              !detalleOcupacionError &&
              detalleOcupacionData?.clientes?.length > 0 && (
                <>
                  <div className="ocu-detail-note">
                    Clientes con posiciones activas en {detalleOcupacion.nombre}. Posiciones detectadas:{" "}
                    <strong>{detalleOcupacionData.posicionesDetectadas}</strong>.
                  </div>

                  <div className="ocu-detail-table-wrap">
                    <table className="ocu-detail-table">
                      <thead>
                        <tr>
                          <th>Cliente</th>
                          <th>Posiciones</th>
                          <th>En excedente</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalleOcupacionData.clientes.map((item) => (
                          <tr key={item.cliente}>
                            <td className="ocu-detail-client">{item.cliente}</td>
                            <td>
                              <strong>{item.posiciones}</strong>
                            </td>
                            <td>
                              <strong
                                className={item.excedente > 0 ? "ocu-detail-excess" : ""}
                              >
                                {item.excedente}
                              </strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

            {!detalleOcupacionLoading &&
              !detalleOcupacionError &&
              detalleOcupacionData &&
              detalleOcupacionData.clientes.length === 0 && (
                <div className="ocu-detail-state">
                  No se encontraron posiciones activas para esta sucursal.
                </div>
              )}
          </>
        )}
      </Modal>

      {/* Modal Crear/Editar ocupación */}
      <Modal
        open={modalOpen}
        title={editing ? `Editar sucursal` : `Crear sucursal`}
        onClose={() => { if (!ocuSavingRef.current) setModalOpen(false); }}
        footer={
          <>
            
            <button className="btn" type="button" onClick={() => setModalOpen(false)} disabled={ocuSaving}>
              <FiX /> Cancelar
            </button>
            <button className="btn btn-primary" type="button" onClick={guardarOcupacion} disabled={ocuSaving}>
              <FiSave /> {ocuSaving ? "Guardando…" : "Guardar"}
            </button>
          </>
        }
      >
        {ocuSaveError && <div role="alert" style={{ color: "var(--danger)", marginBottom: 12 }}>{ocuSaveError}</div>}
        <div className="field">
          <label>Binder</label>
          <select
            className="input"
            value={form.deposito || ocupacionDeposito}
            onChange={(e) =>
              setForm((s) => ({
                ...s,
                deposito: normalizeOcupacionDeposito(e.target.value),
              }))
            }
            disabled={!!editing || ocuSaving}
          >
            <option value="binder1">Binder 1</option>
            <option value="binder2">Binder 2</option>
          </select>
          <div className="ocu-form-note">
            {editing
              ? `Esta sucursal pertenece a ${binderLabel(form.deposito || ocupacionDeposito)}.`
              : "La sucursal se guardará en el binder seleccionado."}
          </div>
        </div>

        <div className="field">
          <label>Nombre</label>
          <input
            className="input"
            value={form.nombre}
            disabled={ocuSaving}
            onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))}
            placeholder="Ej: Anexo, CA, Fiscal 1…"
          />
        </div>

        <div className="field">
          <label>Ocupación total</label>
          <input
            className="input"
            type="number"
            min="0"
            value={form.ocupacionTotal}
            disabled={ocuSaving}
            onChange={(e) => setForm((s) => ({ ...s, ocupacionTotal: e.target.value }))}
            placeholder="Ej: 120"
          />
          
        </div>

        
      </Modal>
    </div>
  );
}
